import { TypeaheadItem } from '@app/shared/ui/typeahead/interfaces';
import { DEFAULT_PAGINATION, DEFAULT_SUPABASE_QUERY } from '@app/shared/utils/constants';
import { DEFAULT_QUERY_FILTERS } from '@app/shared/utils/constants/query-filters';
import { Pagination, QueryFilters, SupabaseSelectData } from '@app/shared/utils/interfaces';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import { ProductsActions } from './products.actions';
import { ProductsEntity } from './products.models';

export const PRODUCTS_FEATURE_KEY = 'products';

export interface ProductsState extends EntityState<ProductsEntity> {
  selectedId?: ProductsEntity['id']; // which Products record has been selected
  loading: boolean; // has the Products list been loading
  loaded: boolean;
  // filters
  queryFilters: QueryFilters;
  pagination: Pagination;
  total: number;
  supabaseQuery: SupabaseSelectData;
  // filters collections
  sizes: TypeaheadItem<string>[];
  error?: string | null; // last known error (if any)
}

export interface ProductsPartialState {
  readonly [PRODUCTS_FEATURE_KEY]: ProductsState;
}

export const productsAdapter: EntityAdapter<ProductsEntity> = createEntityAdapter<ProductsEntity>();

export const initialProductsState: ProductsState = productsAdapter.getInitialState({
  loading: false,
  loaded: false,
  // filters
  pagination: DEFAULT_PAGINATION,
  total: 0,
  queryFilters: DEFAULT_QUERY_FILTERS,
  supabaseQuery: DEFAULT_SUPABASE_QUERY,
  // filters collections
  sizes: [],
});

const reducer = createReducer(
  initialProductsState,
  // get
  on(ProductsActions.loadProducts, state => ({ ...state, loading: true, loaded: false, error: null })),
  on(ProductsActions.loadProductsSuccess, (state, { products, total }) =>
    productsAdapter.setAll(products, { ...state, total, loading: false, loaded: true }),
  ),
  // load more
  on(ProductsActions.loadMoreProducts, state => ({ ...state, loading: true, error: null })),
  on(ProductsActions.loadMoreProductsSuccess, (state, { products, total }) =>
    productsAdapter.upsertMany(products, { ...state, total, loading: false }),
  ),
  // load single product
  on(ProductsActions.loadProduct, state => ({ ...state, loading: true, error: null })),
  on(ProductsActions.loadProductSuccess, (state, { product }) =>
    productsAdapter.upsertOne(product, { ...state, loading: false }),
  ),

  // load sizes
  on(ProductsActions.loadSizes, state => ({ ...state, loading: true, error: null })),
  on(ProductsActions.loadSizesSuccess, (state, { sizes }) => ({ ...state, sizes, loading: false })),

  // set selected
  on(ProductsActions.setSelectedProduct, (state, { id }) => ({
    ...state,
    selectedId: id,
  })),
  // set query filters
  on(
    ProductsActions.setSupabaseQueryParams,
    (state, { queryParams: { textSearch, sizes, currentPage, itemsPerPage } }) => ({
      ...state,
      queryFilters: { textSearch, sizes },
      pagination: { currentPage, itemsPerPage },
      loaded: false,
    }),
  ),
  // set pagination
  on(ProductsActions.setPagination, (state, { pagination: { currentPage, itemsPerPage } }) => ({
    ...state,
    pagination: { currentPage, itemsPerPage },
  })),
  // error
  on(ProductsActions.productsFailure, (state, { error }) => ({ ...state, error })),
);

export function productsReducer(state: ProductsState | undefined, action: Action) {
  return reducer(state, action);
}
